/// <amd-module name="Wit/WorkItemForm/Details/WorkItemFormDiscussionGroup" />
import { CommentReactionType } from "Comments/Common/Contracts";
import { IComment, ICommentArtifactInfo } from "Comments/Common/Contracts/ICommentsClient";
import * as CommonResources from "Comments/Common/Resources";
import { CommentEditor } from "Comments/Editor/Components/CommentEditor";
import { IRenderEditorProps } from "Comments/Editor/Components/CommentEditor.Props";
import { DisplayNameStorageKeyTranslationService, translateDisplayNamesToStorageKeys } from "Mention/Common/DisplayNameStorageKeyTranslationService";
import { ObservableValue } from "VSS/Core/Observable";
import { EmptyGuidString, format } from "VSS/Core/Util/String";
import { IIdentity } from "VSS/Features/Identities/IdentityService";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { ErrorBoundary } from "VSS/Platform/Components/ErrorBoundary";
import { FormatComponent } from "VSS/Platform/Components/Format/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPageService } from "VSS/Platform/Page";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Button } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Spinner } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";
import * as WorkItemTracking from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CommentFormat, CommentMention } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { getDateInUserTimeZone } from "Wit/Common/DateUtils";
import { CoreField, CoreFieldRefNames, DalFields } from "Wit/Common/Generated/Constants";
import * as WorkItemUtils from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { HtmlEditor } from "Wit/HtmlEditor/Components/HtmlEditor";
import * as Resources from "Wit/WorkItemForm/Resources";
import { convertHTMLToMarkdown } from "Wit/WorkItemFormMarkdown/MarkdownUtils";
import { WorkItemFormMarkdownEditor, WorkItemFormMarkdownEditorMemoized } from "Wit/WorkItemFormMarkdown/WorkItemFormMarkdownEditor";
import { WorkItemFormMarkdownToolbar } from "Wit/WorkItemFormMarkdown/WorkItemFormMarkdownToolbar";
import { IWorkItem, IWorkItemInfoText, WorkItemDiscussionFormat } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemEventData, IWorkItemEventService, IWorkItemEventType } from "Wit/WorkItemModel/Services/IWorkItemEventService";
import * as React from "react";
import { CollapsibleSection, FullScreenSectionContext } from "../Common/Components";
import { MarkdownConversionPreviewDialog } from "../Common/Components/MarkdownConversionPreviewDialog";
import { IWorkItemFormPageContext, WorkItemFormPageContext, useWorkItem } from "../Common/Hooks";
import { htmlDecode } from "../Common/HtmlUtils";
import { ImageUploader } from "../Common/ImageUploader";
import { ICommentWithReactions, IWorkItemFormCommentService } from "../Common/Services";
import { CommentToConvert } from "../Contracts";
import { createHtmlMention } from "./DiscussionMentionUtils";
import {
    WorkItemFormCommentFormatState,
    WorkItemFormCommentFormatSwitch,
    getCurrentCommentFormatSettingAsync
} from "./WorkItemFormCommentFormatSwitch";
import { WorkItemFormCommentPreview } from "./WorkItemFormCommentPreview";
import { WorkItemFormCommentsShimmer } from "./WorkItemFormCommentsShimmer";
import { WorkItemFormHistoricalComment } from "./WorkItemFormHistoricalComment";

export interface IWorkItemFormDiscussionGroupProps {
    gridLayoutEnabled?: boolean;
    wide?: boolean;
    width?: number;
}

export interface WorkItemFormDiscussionGroupState {
    isNewCommentEmpty: boolean;
    isNewCommentActive: boolean;
    isNewCommentSaving: boolean;
}

const discussionSectionId = "discussion-section";

export const WorkItemFormDiscussionGroup: React.FC<IWorkItemFormDiscussionGroupProps> = props => {
    const { gridLayoutEnabled, wide, width } = props;
    const workItem = useWorkItem();

    const newCommentObservable = React.useRef(new ObservableValue<string>("")).current;

    const sectionSettingsKey = React.useMemo(() => {
        return `${workItem.getFieldByName(CoreFieldRefNames.WorkItemType)}/Discussion-Section`;
    }, [workItem]);

    if (gridLayoutEnabled) {
        return (
            <div className="wit-discussion-group">
                <CollapsibleSection
                    sectionId={discussionSectionId}
                    key={"wit-discussion-group"}
                    label={Resources.DiscussionLabel}
                    settingsKey={sectionSettingsKey}
                >
                    <ErrorBoundary handlerComponentType="tfs.page-error-handler">
                        <WorkItemFormDiscussionGroupInternal newCommentContent={newCommentObservable} />
                    </ErrorBoundary>
                </CollapsibleSection>
            </div>
        );
    } else {
        return (
            <div className={css("wit-discussion-group inline-flex-column", wide && "wide")} style={{ width: `${width}%` }}>
                <CollapsibleSection
                    sectionId={discussionSectionId}
                    key={"wit-discussion-group"}
                    label={Resources.DiscussionLabel}
                    settingsKey={sectionSettingsKey}
                >
                    <ErrorBoundary handlerComponentType="tfs.page-error-handler">
                        <WorkItemFormDiscussionGroupInternal {...props} newCommentContent={newCommentObservable} />
                    </ErrorBoundary>
                </CollapsibleSection>
            </div>
        );
    }
};

interface IWorkItemFormDiscussionGroupInternalProps extends IWorkItemFormDiscussionGroupProps {
    newCommentContent?: ObservableValue<string>;
}

class WorkItemFormDiscussionGroupInternal extends React.Component<IWorkItemFormDiscussionGroupInternalProps, WorkItemFormDiscussionGroupState> {
    public static contextType = WorkItemFormPageContext;
    public context: IWorkItemFormPageContext;

    private workItem: IWorkItem;
    private workItemFormCommentService: IWorkItemFormCommentService;
    private _pageContext: IVssPageContext;
    private editingCommentId = new ObservableValue<number | undefined>(undefined);
    private editingCommentContent = new ObservableValue<string>("");
    private editingCommentFormat = new ObservableValue<CommentFormat | undefined>(undefined);
    private editingCommentError = new ObservableValue<Error | null>(null);
    private commentsToShow: number = 50;
    private newCommentDiv: HTMLDivElement | null;
    private newCommentEditor: HtmlEditor | WorkItemFormMarkdownEditor | undefined;
    private newCommentContent: ObservableValue<string>;
    private newCommentError = new ObservableValue<Error | null>(null);
    private personaDetailsProvider: PersonaDetailsProvider;
    private workItemEventService: IWorkItemEventService;
    private markdownCommentEditor: WorkItemFormMarkdownEditor | null;
    private displayNameTranslationService: DisplayNameStorageKeyTranslationService;
    private commentToConvert = new ObservableValue<CommentToConvert | undefined>(undefined);
    private settingsService: ISettingsService;
    private newCommentFormat = new ObservableValue<WorkItemFormCommentFormatState | undefined>(undefined);
    private isFullScreenInstance: boolean | undefined = undefined;
    private isInFullScreenMode: boolean = false;

    constructor(props: IWorkItemFormDiscussionGroupInternalProps, context: IWorkItemFormPageContext) {
        super(props);

        if (!context.pageContext || context.workItem === null) {
            throw new Error("WorkItemFormPageContext can only be used inside a WorkItemFormPageContextProvider");
        }

        this._pageContext = context.pageContext;
        this.workItem = context.workItem;

        this.workItemFormCommentService = context.pageContext.getService<IWorkItemFormCommentService>("IWorkItemFormCommentService");
        this.displayNameTranslationService = context.pageContext.getService<DisplayNameStorageKeyTranslationService>(
            "display-name-storage-key-translation-service"
        );

        this.personaDetailsProvider = new PersonaDetailsProvider(context.pageContext);

        this.state = { isNewCommentEmpty: true, isNewCommentActive: false, isNewCommentSaving: false };
        this.workItemEventService = context.pageContext.getService<IWorkItemEventService>("IWorkItemEventService");
        this.settingsService = this._pageContext.getService<ISettingsService>("ISettingsService");
        this.newCommentContent =
            this.isMarkdownDiscussionEnabled() && props.newCommentContent ? props.newCommentContent : new ObservableValue<string>("");
    }

    private renderPersona = (i: IdentityRef): JSX.Element => (
        <Persona
            className="margin-8"
            identity={i}
            size={this.isMarkdownDiscussionEnabled() ? PersonaSize.size24 : PersonaSize.size40}
            personaProvider={this.personaDetailsProvider}
        />
    );

    private getWorkItemUrl(): string | undefined {
        if (this.workItem.isNew()) {
            return undefined;
        } else {
            const project = this.workItem.project.name;
            return WorkItemUtils.getWorkItemUrl(this._pageContext, this.workItem.id, project);
        }
    }

    public componentDidMount(): void {
        // Make sure we have initial comments
        if (!this.workItem.isNew()) {
            this.workItemFormCommentService.getComments(this.workItem, this.commentsToShow);
        } else {
            //If the work item is new, subscribe to the first save
            this.workItem.revision.subscribe(this.onNewWorkItemSave);
        }
        this.workItem.revision.subscribe(this.onWorkItemSave);
        document.addEventListener("wif-focus-discussion", this.focusAndScrollToDiscussion);

        this.workItemEventService.subscribe(
            ({ workItem }: IWorkItemEventData) => this.processWorkItemReset(workItem),
            IWorkItemEventType.WorkItemReset
        );
        this.workItemEventService.subscribe(this.onTemplateApplied, IWorkItemEventType.WorkItemTemplateApplied);
        // Retrieve the comment value in case the user switches tabs and it is lost in this component
        if (this.isMarkdownDiscussionEnabled()) {
            const workItemComment = this.workItem.getFieldUpdates()[DalFields.HistoryId];
            if (workItemComment) {
                this.newCommentContent.value = workItemComment;
                this.setState({ isNewCommentEmpty: false });
            }
            this.loadNewCommentFormatPrefference();
        } else {
            this.newCommentFormat.value = WorkItemDiscussionFormat.Html;
        }
    }

    private async loadNewCommentFormatPrefference(): Promise<void> {
        const defaultFormat: WorkItemFormCommentFormatState = "HTML";
        try {
            const settings = await getCurrentCommentFormatSettingAsync(this.settingsService);
            this.newCommentFormat.value = settings.CommentFormatState ?? defaultFormat;
        } catch (e) {
            this.newCommentFormat.value = defaultFormat;
        }
    }

    private onTemplateApplied = async (eventData: IWorkItemEventData) => {
        const { workItem } = eventData;

        if (workItem.id !== this.workItem.id) {
            return;
        }

        const workItemComment = workItem.getFieldUpdates()[DalFields.HistoryId];
        if (!workItemComment) {
            return;
        }

        if (!this.isMarkdownDiscussionEnabled() || this.newCommentFormat.value !== WorkItemDiscussionFormat.Markdown) {
            this.setState({ isNewCommentActive: true, isNewCommentEmpty: false });
            return;
        }

        let processedComment = workItemComment.replace(/<br\s*\/?>/gim, "\n\n");
        processedComment = await convertHTMLToMarkdown(processedComment);
        this.newCommentContent.value = processedComment;

        this.workItem.setFieldValueById(DalFields.HistoryId, processedComment);
        this.setState({ isNewCommentEmpty: false });
    };

    private processWorkItemReset(workItem: IWorkItem): void {
        if (!workItem || !this.workItem || workItem.id !== this.workItem.id || !this.newCommentEditor) {
            return;
        }

        this.newCommentEditor?.setContent("");
        this.newCommentContent.value = "";
        this.editingCommentContent.value = "";
        this.setState({ isNewCommentEmpty: true, isNewCommentActive: false, isNewCommentSaving: false });
    }

    private onNewWorkItemSave = () => {
        this.forceUpdate();
        this.workItem.revision.unsubscribe(this.onNewWorkItemSave);
    };

    private onWorkItemSave = () => {
        if (this.newCommentEditor && !this.workItem.getFieldUpdates()[DalFields.HistoryId]) {
            this.newCommentEditor.setContent("");
            this.newCommentContent.value = "";
            this.editingCommentContent.value = "";
            this.setState({ isNewCommentEmpty: true, isNewCommentActive: false, isNewCommentSaving: false });
        }
        this.updateComments();
    };

    public componentWillUnmount(): void {
        document.removeEventListener("wif-focus-discussion", this.focusAndScrollToDiscussion);
        this.workItem.revision.unsubscribe(this.onWorkItemSave);
        this.workItem.revision.unsubscribe(this.onNewWorkItemSave);
        this.workItemEventService.unsubscribe(
            ({ workItem }: IWorkItemEventData) => this.processWorkItemReset(workItem),
            IWorkItemEventType.WorkItemReset
        );
        this.workItemEventService.unsubscribe(this.onTemplateApplied, IWorkItemEventType.WorkItemTemplateApplied);
    }

    private renderNewCommentButtons = (): JSX.Element => {
        return (
            <Observer workItemInfoText={this.workItem.infoText}>
                {(props: { workItemInfoText: IWorkItemInfoText }) => {
                    return (
                        <div className="comment-message-button-area">
                            <Button
                                ariaLabel={CommonResources.Cancel}
                                className={"editor-cancel-button"}
                                onClick={this.onCancelCommentClick}
                                disabled={this.state.isNewCommentSaving}
                                text={CommonResources.Cancel}
                            />
                            <Button
                                ariaLabel={Resources.Save}
                                onClick={this.onSaveCommentClick}
                                disabled={props.workItemInfoText.invalid || this.state.isNewCommentEmpty || this.state.isNewCommentSaving}
                                primary
                                text={Resources.Save}
                            />
                        </div>
                    );
                }}
            </Observer>
        );
    };

    private onSaveCommentClick = (): void => {
        const comment = this.newCommentEditor!.getValue();
        this.setState({ isNewCommentSaving: true });
        this.newCommentEditor?.setEnabled(false);
        this.addComment(comment);
    };

    private onCancelCommentClick = (): void => {
        this.userChangedValue("");
        this.newCommentEditor?.setContent("");
        this.newCommentContent.value = "";
        this.setState({ isNewCommentEmpty: true, isNewCommentActive: false });
    };

    private getCommentPreview = (content: ObservableValue<string>, commentFormat?: ObservableValue<CommentFormat>): React.ReactNode =>
        this.isMarkdownDiscussionEnabled() && (
            <Observer commentContent={content} commentFormat={commentFormat}>
                {(props: { commentContent: string; commentFormat: CommentFormat }) => {
                    return commentFormat?.value === CommentFormat.Markdown ? (
                        <WorkItemFormCommentPreview
                            content={props.commentContent}
                            ariaLabel={Resources.MarkdownCommentPreview}
                            options={{ linkifyImages: this.isLinkifyImagesInMarkdownEnabled() }}
                        />
                    ) : null;
                }}
            </Observer>
        );

    private getNewCommentEditor(
        className: string,
        imageUploader: ImageUploader,
        onChange: (s: string, markdownFormat: boolean) => void,
        newCommentFormat: WorkItemFormCommentFormatState,
        ref?: React.LegacyRef<HtmlEditor> | React.Ref<WorkItemFormMarkdownEditor>
    ): JSX.Element {
        const fieldUpdates: string | undefined = this.workItem.getFieldUpdates()[DalFields.HistoryId];
        let htmlContent = this.newCommentEditor ? this.newCommentEditor.getValue() : "";
        if (fieldUpdates) {
            htmlContent = fieldUpdates;
        }
        const key = `wit-${className}${this.workItem.revision.value}`;

        return (
            <div className={css("comment-editor", className)}>
                {this.isMarkdownDiscussionEnabled() && newCommentFormat === WorkItemDiscussionFormat.Markdown ? (
                    <Observer newCommentError={this.newCommentError}>
                        {(observerProps: { newCommentError: Error }) => {
                            return (
                                <>
                                    <WorkItemFormMarkdownEditorMemoized
                                        key={key}
                                        className={css("markdown-editor", className)}
                                        value={this.newCommentContent}
                                        placeholder={Resources.WorkItemDiscussionAddComment}
                                        ariaLabel={Resources.DiscussionLabel}
                                        onChange={content => {
                                            this.newCommentContent.value = content;
                                            onChange(content, true);
                                        }}
                                        ref={ref as React.Ref<WorkItemFormMarkdownEditor>}
                                        onFocus={this.onNewCommentFocus}
                                        onBlur={this.onNewCommentBlur}
                                        uploadImage={file => imageUploader.upload(file)}
                                        error={observerProps.newCommentError}
                                        onError={error => (this.newCommentError.value = error)}
                                        oppositeMessageText={
                                            this.isMarkdownDiscussionEnabled() && (
                                                <WorkItemFormCommentFormatSwitch
                                                    currentFormat={newCommentFormat}
                                                    switch={newFormat => {
                                                        this.newCommentFormat.value = newFormat;
                                                    }}
                                                />
                                            )
                                        }
                                        autoAdjustHeight={!(this.isInFullScreenMode && !this.isFullScreenInstance)}
                                    />
                                    <div className="flex-row flex-wrap toolbal-area">
                                        <WorkItemFormMarkdownToolbar
                                            pageContext={this._pageContext}
                                            getTextSource={() => this.newCommentEditor as WorkItemFormMarkdownEditor}
                                            uploadImage={file => imageUploader.upload(file)}
                                            hide={!this.state.isNewCommentActive}
                                            debounceHideMs={100}
                                        />
                                        {this.isNewCommentButtonsAvailable() && this.renderNewCommentButtons()}
                                    </div>
                                </>
                            );
                        }}
                    </Observer>
                ) : (
                    <>
                        <HtmlEditor
                            className={className}
                            key={key}
                            ariaLabel={Resources.DiscussionLabel}
                            placeholder={Resources.WorkItemDiscussionAddComment}
                            htmlContent={htmlContent}
                            helpText={Resources.InviteToComment}
                            onChange={content => {
                                this.newCommentContent.value = content;
                                onChange(content, false);
                            }}
                            onFocus={this.onNewCommentFocus}
                            onBlur={this.onNewCommentBlur}
                            ref={ref as React.LegacyRef<HtmlEditor>}
                            telemetryContext={{ controlName: "HtmlEditor" }}
                            showChromeBorder={true}
                            uploadImageHandler={file => imageUploader.upload(file)}
                        />
                        {this.isMarkdownDiscussionEnabled() ? (
                            <div className="html-switch-to-markdown-message">
                                <WorkItemFormCommentFormatSwitch
                                    currentFormat={newCommentFormat}
                                    switch={newFormat => {
                                        this.newCommentFormat.value = newFormat;
                                    }}
                                />
                            </div>
                        ) : null}
                        {this.isNewCommentButtonsAvailable() && this.renderNewCommentButtons()}
                    </>
                )}
                {this.getCommentPreview(
                    this.newCommentContent,
                    new ObservableValue(
                        this.isMarkdownDiscussionEnabled() && newCommentFormat === WorkItemDiscussionFormat.Markdown
                            ? CommentFormat.Markdown
                            : CommentFormat.Html
                    )
                )}
            </div>
        );
    }

    private isHtmlEmpty(value: string): boolean {
        if (!value) {
            return true;
        }

        // We check for contents.length for performance reason. String replace is not very performant call and we dont want to run it on long strings.
        // 100 seems to be long enough for text entered by mistake. We also dont want to not let user enter empty tags if he really wants to.
        if (value.length >= 100) {
            return false;
        }

        const doc = new DOMParser().parseFromString(value, "text/html").documentElement.textContent;
        return doc?.trim().length === 0;
    }

    private onFullScreenToggled(fullScreenContent?: JSX.Element): void {
        if (!fullScreenContent) {
            this.isFullScreenInstance = false;
            this.isInFullScreenMode = false;
            return;
        }

        const isInFullScreen = fullScreenContent.props.sectionId === discussionSectionId;

        if (isInFullScreen && this.isFullScreenInstance === undefined) {
            this.isFullScreenInstance = true;
        }

        this.isInFullScreenMode = isInFullScreen;
    }

    public render(): JSX.Element {
        const pageData = this._pageContext.getService<IVssPageService>("IVssPageService").getData()!;
        const currentUser = pageData.user;
        const imageUploader: ImageUploader = new ImageUploader(this._pageContext, this.workItem);
        const commentsObservable = this.workItemFormCommentService.getObservable(this.workItem);

        return (
            <>
                <Observer commentToConvert={this.commentToConvert}>
                    {(props: { commentToConvert: CommentToConvert }) => {
                        if (props.commentToConvert && !props.commentToConvert.markdownComment) {
                            return <MarkdownConversionPreviewDialog commentToConvert={this.commentToConvert} />;
                        }
                        return null;
                    }}
                </Observer>
                {this.isNewCommentAvailable() && (
                    <Observer newCommentFormat={this.newCommentFormat}>
                        {(observerProps: { newCommentFormat: WorkItemFormCommentFormatState | undefined }) => (
                            <div
                                className={css(
                                    "flex-row",
                                    "discussion-new-comment",
                                    this.isMarkdownDiscussionEnabled() && "depth-8 markdown-discussion-comment",
                                    this.isMarkdownDiscussionEnabled() &&
                                        observerProps.newCommentFormat === WorkItemDiscussionFormat.Html &&
                                        "html-discussion-comment",
                                    !this.isMarkdownDiscussionEnabled() && "html-discussion-comment"
                                )}
                            >
                                {this.renderPersona(currentUser)}
                                <FullScreenSectionContext.Consumer>
                                    {({ fullScreenContent }) => {
                                        return (
                                            <div
                                                key="new-comment-observer"
                                                className={css("new-comment-div flex-column", fullScreenContent && "fullscreen-enabled")}
                                                ref={ref => (this.newCommentDiv = ref)}
                                            >
                                                <Observer watermark={this.workItem.revision}>
                                                    {() =>
                                                        observerProps.newCommentFormat ? (
                                                            <>
                                                                {this.getNewCommentEditor(
                                                                    "enter-new-comment",
                                                                    imageUploader,
                                                                    (newValue: string, markdownFormat: boolean) => {
                                                                        const isEmpty = markdownFormat
                                                                            ? !newValue || newValue.trim().length === 0
                                                                            : this.isHtmlEmpty(newValue);
                                                                        const isNewValueEmpty = !newValue;

                                                                        if (!isEmpty) {
                                                                            this.userChangedValue(newValue);
                                                                        } else {
                                                                            const fieldUpdates: string | undefined =
                                                                                this.workItem.getFieldUpdates()[DalFields.HistoryId];
                                                                            if (fieldUpdates) {
                                                                                this.userChangedValue("");
                                                                            }
                                                                        }

                                                                        if (
                                                                            this.state.isNewCommentActive === isNewValueEmpty ||
                                                                            this.state.isNewCommentEmpty !== isEmpty
                                                                        ) {
                                                                            this.setState({
                                                                                isNewCommentEmpty: isEmpty,
                                                                                isNewCommentActive: !isNewValueEmpty
                                                                            });
                                                                        }
                                                                    },
                                                                    observerProps.newCommentFormat,
                                                                    (ref: HtmlEditor | WorkItemFormMarkdownEditor | null) => {
                                                                        if (ref) {
                                                                            this.newCommentEditor = ref;
                                                                        }
                                                                    }
                                                                )}
                                                            </>
                                                        ) : (
                                                            <WorkItemFormCommentsShimmer newComment />
                                                        )
                                                    }
                                                </Observer>
                                            </div>
                                        );
                                    }}
                                </FullScreenSectionContext.Consumer>
                            </div>
                        )}
                    </Observer>
                )}
                {!this.workItem.isNew() && (
                    <Observer key="historical-comments-observer" comments={commentsObservable} editingCommentId={this.editingCommentId}>
                        {(props: { comments: ICommentWithReactions[]; editingCommentId: number | undefined }) => {
                            let returnValue: (JSX.Element | null)[] = props.comments.map(commentWithReactions => {
                                const c = commentWithReactions.comment;
                                const comment: IComment = {
                                    id: c.id,
                                    createdDate: getDateInUserTimeZone(c.createdDate),
                                    createdBy: c.createdBy,
                                    createdOnBehalfDate: c.createdOnBehalfDate ? getDateInUserTimeZone(c.createdOnBehalfDate) : undefined,
                                    createdOnBehalfOf: c.createdOnBehalfOf,
                                    modifiedDate: getDateInUserTimeZone(c.modifiedDate),
                                    text: c.text,
                                    version: c.version,
                                    isDeleted: false,
                                    reactions: [],
                                    format: new ObservableValue(c.format)
                                };
                                const key = "wit-historical-comment-" + comment.id + "-" + comment.version;
                                if (this.editingCommentId.value !== undefined && comment.id == props.editingCommentId) {
                                    if (this.editingCommentContent.value !== "") {
                                        comment.text = this.editingCommentContent.value;
                                    }
                                    return (
                                        <div
                                            className={css(
                                                "flex-row",
                                                this.isMarkdownDiscussionEnabled() && "depth-8 markdown-discussion-comment",
                                                this.isMarkdownDiscussionEnabled() && location.hash === "#" + comment.id && "highlighted"
                                            )}
                                            key={key}
                                        >
                                            {this.renderPersona(currentUser)}
                                            {this.renderCommentEditor(key, comment, imageUploader)}
                                        </div>
                                    );
                                } else {
                                    const baseUrl = location.origin + this.getWorkItemUrl();
                                    let canEdit =
                                        comment.createdBy.id === currentUser.id && !props.editingCommentId && this.workItem.isCommentingAvailable();
                                    const artifactInfo: ICommentArtifactInfo = {
                                        artifactKind: "workitemcomment",
                                        artifactId: "" + comment.id,
                                        projectId: this.workItem.project.guid
                                    };
                                    return (
                                        <WorkItemFormHistoricalComment
                                            artifactInfo={artifactInfo}
                                            canEdit={canEdit}
                                            isReactionsEnabled={!this.workItem.isReadOnly()}
                                            comment={comment}
                                            baseUrl={baseUrl}
                                            key={key}
                                            imageUploader={imageUploader}
                                            commentWithReactions={commentWithReactions}
                                            pageContext={this._pageContext}
                                            renderPersona={this.renderPersona}
                                            createCommentReaction={this.createCommentReaction}
                                            cancelEdit={this.cancelEdit}
                                            deleteComment={this.deleteComment}
                                            deleteCommentReaction={this.deleteCommentReaction}
                                            startEdit={this.startEdit}
                                            update={this.update}
                                            getRecentlyReactedUsers={this.getRecentlyReactedUsers}
                                        />
                                    );
                                }
                            });
                            return returnValue;
                        }}
                    </Observer>
                )}
                {!this.workItem.isNew() && (
                    <Observer ready={commentsObservable.ready}>
                        {(props: { ready: boolean }) => {
                            if (!props.ready) {
                                // Loading initial, or loading more
                                return this.isMarkdownDiscussionEnabled() ? <WorkItemFormCommentsShimmer count={3} /> : <Spinner />;
                            }
                            return this.renderShowMoreLink();
                        }}
                    </Observer>
                )}
            </>
        );
    }

    private isNewCommentAvailable(): boolean {
        return this.workItem.isCommentingAvailable();
    }

    private isMarkdownDiscussionEnabled(): boolean {
        return isFeatureFlagEnabled(this._pageContext, FeatureFlags.IsMarkdownDiscussionEnabled, false);
    }

    private isLinkifyImagesInMarkdownEnabled(): boolean {
        return isFeatureFlagEnabled(this._pageContext, FeatureFlags.LinkifyImageMarkdown, false);
    }

    private isNewCommentButtonsAvailable(): boolean {
        return this.workItem.isCommentingAvailable() && this.state.isNewCommentActive && !this.workItem.isNew();
    }

    private setMarkdownCommentEditor(editor: WorkItemFormMarkdownEditor | null): void {
        this.markdownCommentEditor = editor;
    }

    private renderCommentEditor(key: string, comment: IComment, imageUploader: ImageUploader): JSX.Element {
        if (comment.format?.value === CommentFormat.Markdown && this.isMarkdownDiscussionEnabled()) {
            const newValue = htmlDecode(comment.text);
            if (this.editingCommentContent.value !== newValue) {
                this.editingCommentContent.value = newValue;
            }
        } else {
            const newValue = comment.text;
            if (this.editingCommentContent.value !== newValue) {
                this.editingCommentContent.value = newValue;
            }
        }

        this.editingCommentFormat.value = comment.format?.value;
        return (
            <div className="comment-wrapper">
                <CommentEditor
                    key={key + "-edit"}
                    className="displayed-comment"
                    comment={comment}
                    originalText={comment.text}
                    onCancelEdit={(commentId: number, isDirty: boolean) => {
                        this.cancelEdit(commentId, isDirty);
                    }}
                    onUpdate={(commentId: number, newValue: string, oldValue: string) => {
                        this.update(commentId, newValue, oldValue, comment.format);
                    }}
                    infoMessage={
                        this.isMarkdownDiscussionEnabled() && (
                            <Observer commentFormat={comment.format}>
                                {(propsFormat: { commentFormat: CommentFormat }) => {
                                    return propsFormat.commentFormat === CommentFormat.Html ? (
                                        <FormatComponent format={Resources.WorkItemCommentConvertToMarkdown}>
                                            {[
                                                <Link
                                                    key="convert-to-markdown-key"
                                                    onClick={() => {
                                                        this.commentToConvert.value = {
                                                            htmlComment: this.editingCommentContent.value
                                                        };
                                                        const onCommentChange = (value: CommentToConvert) => {
                                                            if (value?.markdownComment !== undefined) {
                                                                comment.format!.value = CommentFormat.Markdown;
                                                                this.editingCommentContent.value = value.markdownComment;
                                                            }
                                                            this.commentToConvert.unsubscribe(onCommentChange);
                                                        };
                                                        this.commentToConvert.subscribe(onCommentChange);
                                                    }}
                                                >
                                                    {Resources.WorkItemCommentConvertToMarkdownLinkText}
                                                </Link>
                                            ]}
                                        </FormatComponent>
                                    ) : (
                                        <WorkItemFormMarkdownToolbar
                                            pageContext={this._pageContext}
                                            getTextSource={() => this.markdownCommentEditor}
                                            uploadImage={file => imageUploader.upload(file)}
                                        />
                                    );
                                }}
                            </Observer>
                        )
                    }
                    onRenderEditor={(props: IRenderEditorProps) => {
                        const htmlEditor = (
                            <HtmlEditor
                                key={key + "-edit-html"}
                                className={"edit-comment"}
                                htmlContent={props.comment.text}
                                helpText={Resources.InviteToComment}
                                onChange={props.onChange}
                                telemetryContext={{ controlName: "HtmlEditor" }}
                                showChromeBorder={true}
                                editOnlyMode={true}
                                uploadImageHandler={file => imageUploader.upload(file)}
                                ref={comp => {
                                    props.ref(comp);
                                }}
                            />
                        );
                        return this.isMarkdownDiscussionEnabled() ? (
                            <Observer commentFormat={comment.format}>
                                {(propsFormat: { commentFormat: CommentFormat }) => {
                                    return propsFormat.commentFormat == CommentFormat.Markdown ? (
                                        <Observer editingCommentError={this.editingCommentError}>
                                            {(observerProps: { editingCommentError: Error }) => {
                                                return (
                                                    <WorkItemFormMarkdownEditorMemoized
                                                        key={key + "-edit-markdown"}
                                                        className="edit-comment markdown-editor"
                                                        value={this.editingCommentContent}
                                                        placeholder={Resources.InviteToComment}
                                                        ariaLabel={Resources.InviteToComment}
                                                        onChange={content => {
                                                            this.editingCommentContent.value = content;
                                                            props.onChange(content);
                                                        }}
                                                        ref={comp => {
                                                            props.ref(comp);
                                                            this.setMarkdownCommentEditor(comp);
                                                        }}
                                                        uploadImage={file => imageUploader.upload(file)}
                                                        error={observerProps.editingCommentError}
                                                        onError={error => (this.editingCommentError.value = error)}
                                                    />
                                                );
                                            }}
                                        </Observer>
                                    ) : (
                                        htmlEditor
                                    );
                                }}
                            </Observer>
                        ) : (
                            htmlEditor
                        );
                    }}
                />
                {this.getCommentPreview(this.editingCommentContent, comment.format)}
            </div>
        );
    }

    private renderShowMoreLink(): JSX.Element | null {
        const observable = this.workItemFormCommentService.getObservable(this.workItem);
        const commentsHave = observable.value?.length ?? 0;
        const maxComments = this.workItemFormCommentService.getTotalCommentsInWorkItem(this.workItem)!;
        if (commentsHave < maxComments && observable.ready) {
            return (
                <span>
                    <Link
                        key={"" + commentsHave + "-" + maxComments}
                        onClick={() => {
                            this.onClickShowMore();
                        }}
                    >
                        {Resources.ShowMoreCommentsLink}
                    </Link>
                    {" " + format(Resources.ShowMoreCommentsDetail, commentsHave, maxComments)}
                </span>
            );
        } else {
            return null;
        }
    }

    private onClickShowMore(): void {
        this.commentsToShow += 50;
        this.workItemFormCommentService.getComments(this.workItem, this.commentsToShow);
    }

    private startEdit = (commentId: number) => {
        this.editingCommentId.value = commentId;
        this.editingCommentContent.value = "";
    };

    private cancelEdit = (commentId: number, isDirty: boolean) => {
        this.editingCommentId.value = undefined;
        this.editingCommentContent.value = "";
    };

    private async addComment(newValue: string): Promise<void> {
        try {
            let newCommentText = newValue;
            if (this.isMarkdownDiscussionEnabled()) {
                newCommentText = translateDisplayNamesToStorageKeys(this._pageContext, newValue);
            }
            await this.workItemFormCommentService.addComment(this.workItem, newCommentText);
            this.workItem.addLinksForNewMentionsInComment(newCommentText, this.isMarkdownDiscussionEnabled());
            // TODO: Is there a better way to refresh work item data from server?
            this.workItem.revision.value++;
            this.userChangedValue("");
            this.newCommentEditor?.setContent("");
            this.newCommentContent.value = "";
            this.setState({ isNewCommentActive: false });
        } catch (error) {
            this.workItem.setError(error);
        } finally {
            this.setState({ isNewCommentSaving: false });
            this.newCommentEditor?.setEnabled(true);
        }
    }

    private update = (commentId: number, newValue: string, oldValue: string, format?: ObservableValue<CommentFormat>): void => {
        const translatedValue = translateDisplayNamesToStorageKeys(this._pageContext, newValue);
        this.workItemFormCommentService
            .updateComment(this.workItem, commentId, translatedValue, format?.value)
            .then(() => {
                this.workItem.addLinksForNewMentionsInComment(newValue, format?.value === CommentFormat.Markdown);
                this.editingCommentId.value = undefined;
                this.editingCommentContent.value = "";
                // TODO: Is there a better way to refresh work item data from server?
                this.workItem.revision.value++;
            })
            .catch(error => {
                this.workItem.setError(error);
            });
    };

    private getRecentlyReactedUsers = (commentId: number, reactionType: WorkItemTracking.CommentReactionType, top: number): void => {
        this.workItemFormCommentService.getRecentlyReactedUsers(this.workItem, commentId, reactionType, top);
    };

    private deleteComment = (commentId: number): void => {
        this.workItemFormCommentService
            .deleteComment(this.workItem, commentId)
            .then(() => {
                // TODO: Is there a better way to refresh work item data from server?
                this.workItem.revision.value++;
            })
            .catch(error => {
                this.workItem.setError(error);
            });
    };

    private createCommentReaction = (commentId: number, reactionType: CommentReactionType) => {
        this.workItemFormCommentService.createCommentReaction(this.workItem, commentId, reactionType).then(() => {
            this.forceUpdate();
        });
    };

    private deleteCommentReaction = (commentId: number, reactionType: CommentReactionType) => {
        this.workItemFormCommentService.deleteCommentReaction(this.workItem, commentId, reactionType).then(() => {
            this.forceUpdate();
        });
    };

    private userChangedValue(newValue: any) {
        this.workItem.setFieldValueById(CoreField.History, newValue, { checkIsCommentingAvailable: true });
    }

    private updateComments(): void {
        this.workItemFormCommentService.getComments(this.workItem, this.commentsToShow).catch(error => {
            // User should be informed in case of VssVersionOutOfRangeException and he needs to refresh his browser
            // This error can happen in the background, so user can not save his changes, because of discrepancy in API versions
            // between Frontend and Backend
            this.workItem.infoText.value = { text: Resources.FetchErrorRefreshPage, invalid: true };
            console.error(error.message);
        });
    }

    private focusAndScrollToDiscussion = () => {
        if (this.context.focusDiscussion) {
            // Only set focusDiscussion if caused by an event or the editor was found
            if (
                this.newCommentEditor &&
                this.newCommentDiv &&
                window.getComputedStyle(this.newCommentDiv).visibility !== "hidden" &&
                this.newCommentDiv.className.indexOf("fullscreen-enabled") === -1
            ) {
                this.newCommentEditor.focus();
                this.newCommentDiv.scrollIntoView(true);
                this.context.updateFocusDiscussion(false);
            }
        }
    };

    private onNewCommentBlur = (): void => {
        const comment = this.newCommentEditor!.getValue();
        this.setState({ isNewCommentActive: comment != undefined && comment !== "" });
    };

    private createMarkdownMention = (name: string) => `@<${name}> `;

    private getMentionString = (identity: IIdentity, storageKey: string) => {
        const settingsService = this._pageContext.getService<ISettingsService>("ISettingsService");
        const formatSettings: string | undefined = settingsService.getEntry<string>(
            "WorkItemDiscussionSettings/CommentFormatState",
            SettingsUserScope.Me
        );

        if (this.isMarkdownDiscussionEnabled() && formatSettings === WorkItemDiscussionFormat.Markdown) {
            const displayText = this.displayNameTranslationService.getIdentityId(identity);
            return this.createMarkdownMention(this.displayNameTranslationService.addDisplayNameAndStorageKey(displayText, storageKey));
        } else {
            return createHtmlMention(this._pageContext, identity);
        }
    };

    private onNewCommentFocus = async () => {
        this.setState({ isNewCommentActive: true });

        if (isFeatureFlagEnabled(this._pageContext, "WebAccess.WorkItemTracking.MentionsReplyAll", false)) {
            const comments = this.workItemFormCommentService.getObservable(this.workItem).value;

            if (comments.length) {
                const latestComment = comments[0].comment;

                const mentions = latestComment.mentions ?? [];
                const targets = new Set<string>();
                const uniqueIdentityMentions: CommentMention[] = [];

                for (const mention of mentions) {
                    if (mention.artifactType !== "Person") {
                        continue;
                    }
                    if (targets.has(mention.targetId)) {
                        continue;
                    }

                    uniqueIdentityMentions.push(mention);
                }

                const lastCommentAuthor = latestComment.createdBy;

                const pageData = this._pageContext.getService<IVssPageService>("IVssPageService").getData()!;
                const currentUser = pageData.user;

                if (uniqueIdentityMentions.length > 0 && lastCommentAuthor && currentUser && this.newCommentEditor) {
                    const isEmpty = this.newCommentEditor.isEmpty();
                    const authorName = lastCommentAuthor.displayName;
                    const authorId = lastCommentAuthor.id;
                    const shouldReplyAll = uniqueIdentityMentions.some(mention => mention.targetId.toUpperCase() === currentUser.id.toUpperCase());

                    if (isEmpty && shouldReplyAll && authorName && authorId && authorId !== EmptyGuidString) {
                        const authorIdentity = await this.personaDetailsProvider.getEntityFromUniqueAttribute(authorId);
                        let mentionString = this.getMentionString(authorIdentity, authorId);

                        for (const mention of uniqueIdentityMentions) {
                            if (
                                mention.targetId.toUpperCase() === authorId.toUpperCase() ||
                                mention.targetId.toUpperCase() === currentUser.id.toUpperCase()
                            ) {
                                continue;
                            }
                            const identity = await this.personaDetailsProvider.getEntityFromUniqueAttribute(mention.targetId);
                            mentionString += this.getMentionString(identity, mention.targetId);
                        }

                        // Don't get rid of user content if they started typing quickly
                        if (this.newCommentEditor.isEmpty()) {
                            this.newCommentEditor.setContent(mentionString);

                            // Set cursor to end
                            this.newCommentEditor.selectAll();
                            window.getSelection()?.collapseToEnd();
                        }
                    }
                }
            }
        }
    };
}
